import { AgentRunDto } from "@growth/dto";
import type { ActivityKind } from "@growth/schema";
import {
  Brain,
  CircleCheck,
  FileSearch,
  Globe,
  ImageIcon,
  ListChecks,
  LoaderCircle,
  PenLine,
  Play,
  Scale,
  Search,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";

import { clock, isWorking, runStatus, useAgentRun, useTicking } from "../lib/agent-runs";

const ICONS: Record<ActivityKind, typeof Play> = {
  start: Play,
  read: Globe,
  search: Search,
  think: Brain,
  write: PenLine,
  check: ListChecks,
  render: ImageIcon,
  decide: Scale,
  done: CircleCheck,
  warn: TriangleAlert,
};

/** A small pulsing dot: something is happening. */
export const LiveDot = ({ className = "" }: { className?: string }) => (
  <span className={`relative inline-flex size-2.5 shrink-0 ${className}`} aria-hidden="true">
    <span className="absolute inline-flex size-full animate-ping rounded-full bg-accent opacity-60" />
    <span className="relative inline-flex size-2.5 rounded-full bg-accent" />
  </span>
);

/**
 * What an agent is doing, step by step, newest at the bottom. While it works, the current step
 * pulses and counts the seconds it's been on it, so a long think never looks frozen.
 */
export const AgentActivity = ({
  run,
  limit = 6,
  bare = false,
}: {
  run: AgentRunDto;
  /** Steps shown before "Show earlier steps". */
  limit?: number;
  /** Without the card frame and header (for places that have their own). */
  bare?: boolean;
}) => {
  const working = isWorking(run.status);
  const now = useTicking(working);
  const [all, setAll] = useState(false);
  const steps = run.activity;
  const hidden = all ? 0 : Math.max(0, steps.length - limit);
  const shown = steps.slice(hidden);
  const start = Date.parse(run.startedAt ?? run.createdAt);

  const list = (
    <ol className="space-y-1.5" aria-live="polite" aria-relevant="additions">
      {hidden > 0 && (
        <li>
          <button
            type="button"
            className="text-xs text-accent underline underline-offset-2"
            onClick={() => setAll(true)}
          >
            Show {hidden} earlier step{hidden === 1 ? "" : "s"}
          </button>
        </li>
      )}
      {shown.length === 0 && working && (
        <li className="flex items-center gap-2 text-sm text-muted">
          <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
          {run.status === "queued" ? "Waiting for a free agent…" : "Getting started…"}
        </li>
      )}
      {shown.map((step, index) => {
        const current = working && index === shown.length - 1;
        const Icon = ICONS[step.kind];
        const at = Date.parse(step.at);
        return (
          <li key={`${step.at}-${index}`} className="flex items-start gap-2.5 text-sm">
            {current ? (
              <LiveDot className="mt-1.5" />
            ) : (
              <Icon
                className={`mt-0.5 size-4 shrink-0 ${
                  step.kind === "warn"
                    ? "text-amber-600"
                    : step.kind === "done"
                      ? "text-emerald-700"
                      : "text-muted"
                }`}
                aria-hidden="true"
              />
            )}
            <span className={`min-w-0 flex-1 ${current ? "font-medium text-ink" : "text-muted"}`}>
              {step.text}
            </span>
            <time
              dateTime={step.at}
              className="shrink-0 pt-0.5 font-mono text-[11px] tabular-nums text-muted"
              title={new Date(at).toLocaleTimeString()}
            >
              {current ? `for ${clock(now - at)}` : clock(at - start)}
            </time>
          </li>
        );
      })}
    </ol>
  );

  if (bare) return list;
  return (
    <section className="space-y-3 rounded-xl border border-line bg-white p-4">
      <header className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="flex items-center gap-2 font-medium text-ink">
          {working ? <LiveDot /> : <FileSearch className="size-4 text-muted" aria-hidden="true" />}
          {run.agentName}
          <span className="font-normal text-muted">· {run.title}</span>
        </span>
        <span className="font-mono text-xs text-muted">{runStatus(run, now)}</span>
      </header>
      {list}
    </section>
  );
};

/** AgentActivity for a run id, followed live. Nothing until the run has loaded. */
export const RunFeed = ({ runId, limit }: { runId: string | null | undefined; limit?: number }) => {
  const run = useAgentRun(runId);
  if (!run.data) return null;
  return <AgentActivity run={run.data} limit={limit} />;
};
